// src/platforms/aws/diagrams/utils/computeFlowLayout.js
//
// Shared flow-layout engine: measures node dimensions (NodeMeasurement) and runs
// dagre with adaptive spacing + collision resolution. Extracted from
// ContactFlowDiagram.js so the layout logic lives in one reusable place.
//
// getLayoutedElements() is the function the React Flow viewer uses to lay out a flow.

import dagre from 'dagre';
import {
  measureNodes,
  measureSimilarNodes,
  optimizeForBatchExport,
  getEstimatedDimensions
} from './NodeMeasurement';

// Fallback layout function for when dagre fails
const getFallbackLayout = (nodes, edges, direction = 'TB') => {
  const isHorizontal = direction === 'LR';
  const nodeSpacing = 200;
  const levelSpacing = 150;
  
  // Simple grid-based layout as fallback
  const layoutedNodes = nodes.map((node, index) => {
    const row = Math.floor(index / 4);
    const col = index % 4;
    
    return {
      ...node,
      position: {
        x: col * nodeSpacing,
        y: row * levelSpacing
      },
      targetPosition: isHorizontal ? 'left' : 'top',
      sourcePosition: isHorizontal ? 'right' : 'bottom'
    };
  });
  
  return { nodes: layoutedNodes, edges };
};

// Enhanced auto-layout function using accurate node measurements to prevent overlapping
const getLayoutedElements = async (nodes, edges, direction = 'TB', options = {}) => {
  const { isBatchExport = false, flowIndex = 0, totalFlows = 1 } = options; // eslint-disable-line no-unused-vars
  // Validate input data
  if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
    console.warn('Invalid or empty nodes array, using fallback layout');
    return getFallbackLayout(nodes || [], edges || [], direction);
  }
  
  if (!edges || !Array.isArray(edges)) {
    console.warn('Invalid edges array, using nodes-only layout');
    edges = [];
  }
  
  // Validate node IDs and filter out invalid nodes
  const validNodes = nodes.filter((node, index) => {
    if (!node || typeof node.id === 'undefined' || node.id === null || node.id === '') {
      return false;
    }
    if (!node.data) {
      node.data = {};
    }
    return true;
  });
  
  // Create node ID set for validation
  const nodeIds = new Set(validNodes.map(n => n.id));
  
  // Validate edges and filter out invalid ones
  const validEdges = edges.filter((edge, index) => {
    if (!edge || !edge.source || !edge.target) {
      return false;
    }
    if (!nodeIds.has(edge.source)) {
      return false;
    }
    if (!nodeIds.has(edge.target)) {
      return false;
    }
    return true;
  });
  
  // If validation removed too many elements, use fallback
  if (validNodes.length === 0) {
    console.warn('No valid nodes after validation, using fallback layout');
    return getFallbackLayout(nodes, edges, direction);
  }
  
  try {
    // Optimize cache for batch exports
    if (isBatchExport && flowIndex === 0) {
      optimizeForBatchExport(200); // Larger cache for batch exports
    }
    
    // Choose optimal measurement strategy
    let nodeDimensions;
    const measurementOptions = { isBatchExport, skipDelay: isBatchExport };
    
    if (isBatchExport && validNodes.length > 20) {
      // Use similarity-based measurement for large batch exports
      nodeDimensions = await measureSimilarNodes(validNodes, measurementOptions);
    } else {
      // Use standard measurement for smaller flows or interactive use
      nodeDimensions = await measureNodes(validNodes, measurementOptions);
    }
    
    // Create a fresh dagre graph for each layout operation to prevent state corruption during bulk exports
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    
    const isHorizontal = direction === 'LR';
    
    // Calculate maximum node dimensions for dynamic spacing
    let maxNodeWidth = 0;
    let maxNodeHeight = 0;
    nodeDimensions.forEach(({ width, height }) => {
      maxNodeWidth = Math.max(maxNodeWidth, width);
      maxNodeHeight = Math.max(maxNodeHeight, height);
    });
    
    // OPTIMIZED: Intelligent adaptive spacing - balances safety with visual density
    const baseNodeSep = isHorizontal ? 100 : 80;
    const baseRankSep = isHorizontal ? 150 : 120;
    
    // Calculate average node dimensions for intelligent spacing decisions
    const averageNodeWidth = Array.from(nodeDimensions.values())
      .reduce((sum, {width}) => sum + width, 0) / nodeDimensions.size;
    const averageNodeHeight = Array.from(nodeDimensions.values())
      .reduce((sum, {height}) => sum + height, 0) / nodeDimensions.size;
    
    // Minimum safe spacing (prevents worst-case overlaps for largest nodes)
    const minSafeNodeSep = maxNodeWidth + Math.max(baseNodeSep * 0.4, 20);
    const minSafeRankSep = maxNodeHeight + Math.max(baseRankSep * 0.4, 15);
    
    // Optimal spacing based on average node sizes (better for typical nodes)
    const optimalNodeSep = averageNodeWidth * 1.3 + Math.max(baseNodeSep * 0.6, 25);
    const optimalRankSep = averageNodeHeight * 1.3 + Math.max(baseRankSep * 0.6, 20);
    
    // Use adaptive spacing: safety first, but optimize when possible
    const dynamicNodeSep = Math.max(minSafeNodeSep, optimalNodeSep);
    const dynamicRankSep = Math.max(minSafeRankSep, optimalRankSep);
    
    
    dagreGraph.setGraph({ 
      rankdir: direction,
      nodesep: dynamicNodeSep,
      ranksep: dynamicRankSep,
      marginx: Math.max(50, maxNodeWidth * 0.2),
      marginy: Math.max(50, maxNodeHeight * 0.2)
    });

    // Apply measured dimensions to dagre graph
    validNodes.forEach((node) => {
      const dimensions = nodeDimensions.get(node.id);
      let width, height;
      
      if (dimensions) {
        width = dimensions.width;
        height = dimensions.height;
      } else {
        // Fallback to the shared estimator if measurement failed
        ({ width, height } = getEstimatedDimensions(node));
      }
      
      // Validate dimensions with more generous bounds
      width = Math.max(50, Math.min(1200, width));
      height = Math.max(30, Math.min(600, height));
      
      // Store dimensions on node for later use
      node.width = width;
      node.height = height;
      
      dagreGraph.setNode(node.id, { width, height });
    });

    validEdges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Apply dagre layout algorithm
    dagre.layout(dagreGraph);
    
    // Apply dagre positions using the validated nodes
    validNodes.forEach((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      
      // Defensive check to prevent "Cannot read properties of undefined" error
      if (!nodeWithPosition) {
        node.position = { x: 0, y: 0 };
        node.targetPosition = isHorizontal ? 'left' : 'top';
        node.sourcePosition = isHorizontal ? 'right' : 'bottom';
        return;
      }
      
      node.targetPosition = isHorizontal ? 'left' : 'top';
      node.sourcePosition = isHorizontal ? 'right' : 'bottom';

      node.position = {
        x: nodeWithPosition.x - (nodeWithPosition.width / 2),
        y: nodeWithPosition.y - (nodeWithPosition.height / 2),
      };
    });
    
    // Enhanced post-processing: Ensure entry node is positioned with adequate spacing from start node
    const entryNode = validNodes.find(n => n.id === 'entry-node');
    const startEdge = validEdges.find(e => e.source === 'entry-node');
    
    if (entryNode && startEdge) {
      const startNode = validNodes.find(n => n.id === startEdge.target);
      if (startNode) {
        const entryWidth = entryNode.width || 80;
        const entryHeight = entryNode.height || 80;
        const startWidth = startNode.width || 100;
        const startHeight = startNode.height || 100;
        
        if (isHorizontal) {
          const spacing = Math.max(200, startWidth + entryWidth + 50);
          entryNode.position.x = startNode.position.x - spacing;
          entryNode.position.y = startNode.position.y + startHeight / 2 - entryHeight / 2;
        } else {
          const spacing = Math.max(180, startHeight + entryHeight + 30);
          entryNode.position.x = startNode.position.x + startWidth / 2 - entryWidth / 2;
          entryNode.position.y = startNode.position.y - spacing;
        }
      }
    }
    
    // Post-layout collision detection and adjustment
    const adjustedNodes = await detectAndResolveCollisions(validNodes, direction);
    
    return { nodes: adjustedNodes, edges: validEdges };
    
  } catch (dagreError) {
    console.error('Dagre layout failed, using fallback layout:', dagreError);
    return getFallbackLayout(validNodes, validEdges, direction);
  }
};

// Collision detection and resolution system
const detectAndResolveCollisions = async (nodes, direction = 'TB') => {
  const isHorizontal = direction === 'LR';
  const adjustedNodes = [...nodes];
  // let collisions = 0; // eslint-disable-line no-unused-vars
  let iterations = 0;
  const maxIterations = 5;
  
  // Validate that all nodes have measured dimensions before proceeding
  const nodesMissingDimensions = nodes.filter(node => 
    !node.width || !node.height || node.width <= 0 || node.height <= 0
  );
  
  if (nodesMissingDimensions.length > 0) {
    // Apply fallback dimensions (shared estimator) to nodes missing measurements
    nodesMissingDimensions.forEach(node => {
      const est = getEstimatedDimensions(node);
      if (!node.width || node.width <= 0) node.width = est.width;
      if (!node.height || node.height <= 0) node.height = est.height;
    });
  }
  
  // Helper function to check if two nodes overlap using their actual measured dimensions
  const nodesOverlap = (nodeA, nodeB) => {
    // Use the measured dimensions stored on the nodes
    const aLeft = nodeA.position.x;
    const aRight = nodeA.position.x + nodeA.width;
    const aTop = nodeA.position.y;
    const aBottom = nodeA.position.y + nodeA.height;
    
    const bLeft = nodeB.position.x;
    const bRight = nodeB.position.x + nodeB.width;
    const bTop = nodeB.position.y;
    const bBottom = nodeB.position.y + nodeB.height;
    
    const hasOverlap = !(aRight <= bLeft || bRight <= aLeft || aBottom <= bTop || bBottom <= aTop);
    
    return hasOverlap;
  };
  
  // Iterative collision resolution using actual measured dimensions
  while (iterations < maxIterations) {
    let collisionsFound = 0;
    
    for (let i = 0; i < adjustedNodes.length; i++) {
      for (let j = i + 1; j < adjustedNodes.length; j++) {
        const nodeA = adjustedNodes[i];
        const nodeB = adjustedNodes[j];
        
        if (nodesOverlap(nodeA, nodeB)) {
          collisionsFound++;
          
          // VALIDATION: Ensure nodes have measured dimensions before collision resolution
          const aWidth = nodeA.width;
          const aHeight = nodeA.height;
          const bWidth = nodeB.width;
          const bHeight = nodeB.height;
          
          if (!aWidth || !aHeight || !bWidth || !bHeight) {
            console.error(`Invalid dimensions for collision: ${nodeA.id}(${aWidth}x${aHeight}) vs ${nodeB.id}(${bWidth}x${bHeight})`);
            continue;
          }
          
          // Calculate overlap amounts using measured dimensions
          const overlapX = Math.min(
            nodeA.position.x + aWidth - nodeB.position.x,
            nodeB.position.x + bWidth - nodeA.position.x
          );
          const overlapY = Math.min(
            nodeA.position.y + aHeight - nodeB.position.y,
            nodeB.position.y + bHeight - nodeA.position.y
          );
          
          // Choose adjustment direction based on layout and add buffer for spacing
          const spacingBuffer = 30; // Consistent spacing buffer
          
          if (isHorizontal) {
            // For horizontal layout, prioritize vertical separation
            if (overlapY < overlapX) {
              // Separate vertically with spacing buffer
              const adjustment = (overlapY / 2) + spacingBuffer;
              if (nodeA.position.y < nodeB.position.y) {
                nodeA.position.y -= adjustment;
                nodeB.position.y += adjustment;
              } else {
                nodeA.position.y += adjustment;
                nodeB.position.y -= adjustment;
              }
            } else {
              // Separate horizontally with spacing buffer  
              const adjustment = (overlapX / 2) + spacingBuffer;
              if (nodeA.position.x < nodeB.position.x) {
                nodeA.position.x -= adjustment;
                nodeB.position.x += adjustment;
              } else {
                nodeA.position.x += adjustment;
                nodeB.position.x -= adjustment;
              }
            }
          } else {
            // For vertical layout, prioritize horizontal separation
            if (overlapX < overlapY) {
              // Separate horizontally with spacing buffer
              const adjustment = (overlapX / 2) + spacingBuffer;
              if (nodeA.position.x < nodeB.position.x) {
                nodeA.position.x -= adjustment;
                nodeB.position.x += adjustment;
              } else {
                nodeA.position.x += adjustment;
                nodeB.position.x -= adjustment;
              }
            } else {
              // Separate vertically with spacing buffer
              const adjustment = (overlapY / 2) + spacingBuffer;
              if (nodeA.position.y < nodeB.position.y) {
                nodeA.position.y -= adjustment;
                nodeB.position.y += adjustment;
              } else {
                nodeA.position.y += adjustment;
                nodeB.position.y -= adjustment;
              }
            }
          }
        }
      }
    }
    
    if (collisionsFound === 0) {
      break;
    }
    
    // collisions += collisionsFound; // eslint-disable-line no-unused-vars
    iterations++;
  }
  
  
  return adjustedNodes;
};

export { getLayoutedElements, getFallbackLayout, detectAndResolveCollisions };
